import { useContext, useEffect, useMemo, useState } from "react";
import { CoverSheetMainPageContext } from "../features/cover-sheet/cover-sheet-main-page-context/context";
import { entryType, formStates, formTypes } from "../constants";
import ClusterActionDialog from "../features/equipment-list/ClusterActionDialog";
import ItemWrapper from "../features/log-book/log-book-item-form-dialog/item-context/ItemWrapper";
import {
  CenteredSpinner,
  getCookieData,
  removeCookieData,
  useSearchParameters
} from "@digitallab/grid-common-frontend-components";
import { useLocation } from "react-router-dom";
import { withApollo, WithApolloClient } from "react-apollo";
import { getEquipDetailInfo, getInstrumentByClusterIdAndSite } from "../features/log-book/LoadLogBooksInfo";

import LogbookSelectedWrapper from "../features/log-book/log-book-table/logBooksSelectAll/LogBookSelectedWrapper";
import ItemCoverSheetWrapper from "../features/log-book/log-book-item-cover-sheet-dialog/cover-sheet-context/ItemCoverSheetWrapper";
import PaginationWrapper from "../components/shared/pagination/PaginationWrapper";
import { InstrumentWithSubEquipmentDetailsModelType } from "../models/DigitalLabLogbookInstrumentModelType";
import { useSelector } from "react-redux";
import { find } from "lodash";
import { RootState } from "../reducers";
import { ILogFormParams } from "./StepperContainer";
import { sanitizeStringNullValue } from "../utils/helpers/text";

export const AddClusterContainer = withApollo(({ client }: { client: WithApolloClient<{}> }) => {
  const { pathname, search } = useLocation();
  const { timestamp = "" } = useSearchParameters(search) as unknown as ILogFormParams;
  const [equipmentDetail, setEquipmentDetail] = useState<undefined | InstrumentWithSubEquipmentDetailsModelType>();
  const equipmentIdList = useMemo(() => {
    const params = new URLSearchParams(search);
    const equipmentIdParam = params.get("equipmentId");
    if (equipmentIdParam) {
      return [equipmentIdParam];
    }

    let cookieData = getCookieData<string[]>(`equipmentIdList${timestamp}`) || [];
    if (cookieData.length) {
      removeCookieData(`equipmentIdList${timestamp}`);
      sessionStorage.setItem(`equipmentIdList${timestamp}`, JSON.stringify(cookieData));
    } else {
      cookieData = JSON.parse(sessionStorage.getItem(`equipmentIdList${timestamp}`) || "[]");
    }
    return cookieData;
  }, [timestamp, search]);
  const isRunLog = pathname.includes("run");

  const storeUser = useSelector((store: { user: { site: string } }) => store.user);
  const systemStatuses = useSelector((state: RootState) => state.runLogsForm.systemStatuses);
  const gxpReadys = useSelector((state: RootState) => state.runLogsForm.gxpReadys);

  const [, setClusterLogStatus] = useState(false);

  type Equipment = {
    gxpReady: string | null | {};
    qualificationStatus: string;
    systemStatus: string | null | {};
    entryType: string;
    equipmentId: string;
    subEquipment: Equipment[];
    configurationBaseline: string | { key: string; value: string; shouldBePublished: boolean };
    softwareVersion: string | { key: string; value: string; shouldBePublished: boolean };
    configurationDetails: string | { key: string; value: string; shouldBePublished: boolean };
  };

  /**
   * Iterate sub-equipment recursively to assign gxpReady, configurationBaseline, softwareVersion, configurationDetails and systemStatus
   * @param subEquipment
   */
  const iterateSubEquipment = (subEquipment: Equipment[]) => {
    subEquipment.forEach((equipment: Equipment) => {
      const isCluster = equipment.entryType === "CLUSTER";
      if (isCluster) {
        iterateSubEquipment(equipment.subEquipment);
      }
      equipment.gxpReady =
        find(gxpReadys, {
          key: equipment.qualificationStatus
        }) || null;
      if (typeof equipment.configurationBaseline === "string" || !equipment.configurationBaseline) {
        equipment.configurationBaseline = {
          key: equipment.configurationBaseline || "",
          value: equipment.configurationBaseline || "",
          shouldBePublished: false
        };
      }
      if (
        typeof equipment.softwareVersion === "string" ||
        !equipment.softwareVersion ||
        equipment.softwareVersion === null
      ) {
        equipment.softwareVersion = {
          key: sanitizeStringNullValue(equipment.softwareVersion) || "",
          value: sanitizeStringNullValue(equipment.softwareVersion) || "",
          shouldBePublished: false
        };
      }
      if (typeof equipment.configurationDetails === "string" || !equipment.configurationDetails) {
        equipment.configurationDetails = {
          key: equipment.configurationDetails || "",
          value: equipment.configurationDetails || "",
          shouldBePublished: false
        };
      }
      equipment.systemStatus =
        find(systemStatuses, {
          value: equipment.systemStatus
        }) || null;
    });
  };

  // Load equipment detail including sub-equipment recursively
  // assign proper gxpReady and systemStatus objects from the store
  useEffect(() => {
    const getEquipments = async () => {
      const getSubEquipmentsDetailInfo = async (id: string, client: object, parentItem = null) => {
        try {
          const result = await getInstrumentByClusterIdAndSite(id, storeUser.site, client);
          for (const item of result.items) {
            if (item.entryType === "CLUSTER") {
              const subs = await getSubEquipmentsDetailInfo(item.inventoryId, client, item);
              if (subs.nested) {
                item.subEquipment = subs.items;
              } else {
                result.items.push(...subs);
              }
            }
          }
          if (parentItem) {
            return { items: result?.items, nested: true };
          }
          return result?.items;
        } catch (err) {
          console.log("errorrrsub", err);
          console.error(err);
        }
      };
      const [foundEquipment, subEquipment] = await Promise.all([
        getEquipDetailInfo(equipmentIdList[0], client),
        getSubEquipmentsDetailInfo(equipmentIdList[0], client)
      ]);
      if (foundEquipment?.selectedLogType === formTypes.ACTION_LOG) {
        foundEquipment.gxpReady =
          find(gxpReadys, {
            key: foundEquipment.gxpReady?.key
          }) || null;
        foundEquipment.systemStatus =
          find(systemStatuses, {
            value: foundEquipment.systemStatus?.value
          }) || null;

        if (foundEquipment.entryType === entryType.cluster) {
          foundEquipment.gxpReadyCluster =
            find(gxpReadys, {
              key: foundEquipment.gxpReadyCluster?.key
            }) || null;
          foundEquipment.systemStatusCluster =
            find(systemStatuses, {
              value: foundEquipment.systemStatusCluster?.value
            }) || null;
        }
      }
      iterateSubEquipment(subEquipment);
      foundEquipment.subEquipment = subEquipment;

      setEquipmentDetail(foundEquipment);
    };
    getEquipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentIdList]);

  const { handleOpenWorkFlowModel, loadFormType } = useContext(CoverSheetMainPageContext);
  handleOpenWorkFlowModel({ isCluster: true, form: formStates.PARTIAL_NEW_FORM });
  loadFormType(isRunLog ? formTypes.RUN_LOG : formTypes.ACTION_LOG);
  return equipmentDetail ? (
    <PaginationWrapper>
      <ItemCoverSheetWrapper>
        <LogbookSelectedWrapper equipmentDetail={equipmentDetail} selectedInstruments={[]}>
          <ItemWrapper entryDataType={equipmentDetail?.entryType} setClusterLogStatus={setClusterLogStatus}>
            <ClusterActionDialog
              type={isRunLog ? formTypes.RUN_LOG : formTypes.ACTION_LOG}
              equipmentDetail={equipmentDetail}
            />
          </ItemWrapper>
        </LogbookSelectedWrapper>
      </ItemCoverSheetWrapper>
    </PaginationWrapper>
  ) : (
    <CenteredSpinner />
  );
});
